/**
 * Regular expression to match URLs in text
 * Matches http://, https://, and www. URLs
 */
const URL_REGEX = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi;

/**
 * Detects URLs in text and returns an array of text segments and links
 * @param text The text to parse
 * @returns Array of objects with type 'text' or 'link' and their content
 */
export function parseTextWithLinks(text: string): Array<{ type: 'text' | 'link'; content: string }> {
  const parts: Array<{ type: 'text' | 'link'; content: string }> = [];
  let lastIndex = 0;

  // Find all matches
  const matches = Array.from(text.matchAll(URL_REGEX));

  matches.forEach((match) => {
    const url = match[0];
    const index = match.index!;

    // Add text before the URL
    if (index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex, index),
      });
    }

    // Add the URL
    parts.push({
      type: 'link',
      content: url,
    });

    lastIndex = index + url.length;
  });

  // Add remaining text after last URL
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.substring(lastIndex),
    });
  }

  // If no URLs found, return the whole text as a single text part
  if (parts.length === 0) {
    parts.push({
      type: 'text',
      content: text,
    });
  }

  return parts;
}

/**
 * Normalizes a URL by adding https:// if it starts with www.
 * @param url The URL to normalize
 * @returns The normalized URL
 */
export function normalizeUrl(url: string): string {
  if (url.startsWith('www.')) {
    return `https://${url}`;
  }
  return url;
}
